import React, { useState, useEffect } from 'react';
import { Grid, Sparkles, LayoutTemplate, Heart, Bookmark, Star } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { WorkGrids, ServiceGrids, BriefGrids } from '../grids';
import { postService, WorkSummary } from '../../../services/postService';
import { Review } from '../../../services/reviewService';
import { ReviewCard } from '../../../components/Contracts/ReviewCard';
import { userService } from '../../../services/userService';
import { useUserReviews, useInteractedWorks } from '../../../hooks/useProfile';
import { resolveProfileShareState } from '../../../utils/share';

type TabType = 'works' | 'services' | 'briefs' | 'reviews' | 'likes' | 'favorites';
const PENDING_WORK_UPLOADS_CHANGED_EVENT = 'pending_work_uploads_changed';

interface ProfileTabsProps {
    isOwnProfile?: boolean;
    userInfo?: { id: string; name: string; avatar: string };
}

const ProfileTabs: React.FC<ProfileTabsProps> = ({ isOwnProfile = false, userInfo }) => {
    const [activeTab, setActiveTab] = useState<TabType>('works');
    const [searchParams] = useSearchParams();

    const [creatorWorks, setCreatorWorks] = useState<WorkSummary[]>([]);
    const [briefsCount, setBriefsCount] = useState<number>(0);
    const [servicesCount, setServicesCount] = useState<number>(0);
    const [creatorWorksLoading, setCreatorWorksLoading] = useState<boolean>(false);
    const [pendingCreatorWorks, setPendingCreatorWorks] = useState<WorkSummary[]>([]);

    // SWR hooks for cached data
    const { likes, favorites, loading: interactionsLoading } = useInteractedWorks(userInfo?.id);
    const { reviews: userReviews, averageRating, reviewCount, refresh: refreshReviews } = useUserReviews(userInfo?.id);
    const [reviewerInfoMap, setReviewerInfoMap] = useState<Record<string, { avatarUrl: string; nickname: string }>>({});
    const shareState = React.useMemo(() => resolveProfileShareState(searchParams), [searchParams]);

    const mergedWorksCount = React.useMemo(() => {
        const ids = new Set<string>();
        creatorWorks.forEach((work) => ids.add(String(work.id)));
        pendingCreatorWorks.forEach((work) => ids.add(String(work.id)));
        return ids.size;
    }, [creatorWorks, pendingCreatorWorks]);

    React.useEffect(() => {
        setPendingCreatorWorks(userInfo?.id ? postService.getPendingWorkUploads(userInfo.id) : []);
    }, [userInfo?.id]);

    useEffect(() => {
        if (shareState.tab) {
            setActiveTab(shareState.tab);
        }
    }, [shareState.tab]);

    React.useEffect(() => {
        if (!userInfo?.id) return;

        const fetchBriefsCount = async () => {
            try {
                const { briefService } = await import('../../../services/briefService');
                const res = await briefService.getBriefs({ clientUserId: userInfo.id, limit: 100, includeArchived: isOwnProfile });
                if (res.data && res.data.briefs) {
                    const backendData = res.data as any;
                    if (backendData.totalCount !== undefined) {
                        setBriefsCount(backendData.totalCount);
                    } else if (backendData.total !== undefined) {
                        setBriefsCount(backendData.total);
                    } else {
                        setBriefsCount(res.data.briefs.length);
                    }
                }
            } catch (error) {
                console.error("Failed to fetch briefs count", error);
            }
        };

        const fetchServicesCount = async () => {
            try {
                const { serviceService } = await import('../../../services/serviceService');
                const res = await serviceService.getServices({ creatorUserId: userInfo.id, limit: 100, includeArchived: isOwnProfile });
                if (res.data && res.data.services) {
                    const backendData = res.data as any;
                    if (backendData.totalCount !== undefined) {
                        setServicesCount(backendData.totalCount);
                    } else if (backendData.total !== undefined) {
                        setServicesCount(backendData.total);
                    } else {
                        setServicesCount(res.data.services.length);
                    }
                }
            } catch (error) {
                console.error('Failed to fetch services count', error);
            }
        };

        const fetchCreatorWorks = async () => {
            setCreatorWorksLoading(true);
            try {
                const creatorPosts = await postService.getCreatorPosts(userInfo.id, 100);
                setCreatorWorks(creatorPosts);
            } catch (error) {
                console.error('Failed to fetch creator works', error);
                setCreatorWorks([]);
            } finally {
                setCreatorWorksLoading(false);
            }
        };

        fetchCreatorWorks();
        fetchBriefsCount();
        fetchServicesCount();

        const handleTabChange = (e: any) => {
            if (e.detail?.tab) {
                setActiveTab(e.detail.tab);
            }
        };

        const handlePostsRefresh = () => {
            void fetchCreatorWorks();
        };

        const handleInteractionsRefresh = () => {
            void fetchCreatorWorks();
        };

        const handlePendingUploadsChange = () => {
            setPendingCreatorWorks(userInfo?.id ? postService.getPendingWorkUploads(userInfo.id) : []);
        };

        window.addEventListener('posts_updated', handlePostsRefresh);
        window.addEventListener('interactions_updated', handleInteractionsRefresh);
        window.addEventListener(PENDING_WORK_UPLOADS_CHANGED_EVENT, handlePendingUploadsChange);
        window.addEventListener('change_profile_tab', handleTabChange);
        window.addEventListener('briefs_updated', fetchBriefsCount);
        window.addEventListener('services_updated', fetchServicesCount);
        return () => {
            window.removeEventListener('posts_updated', handlePostsRefresh);
            window.removeEventListener('interactions_updated', handleInteractionsRefresh);
            window.removeEventListener(PENDING_WORK_UPLOADS_CHANGED_EVENT, handlePendingUploadsChange);
            window.removeEventListener('change_profile_tab', handleTabChange);
            window.removeEventListener('briefs_updated', fetchBriefsCount);
            window.removeEventListener('services_updated', fetchServicesCount);
        };
    }, [userInfo?.id, isOwnProfile]);

    // Fetch reviewer avatars when reviews change
    useEffect(() => {
        if (userReviews.length === 0) return;
        const ids = userReviews.map((r: Review) => r.reviewerUserId);
        if (ids.length > 0) {
            userService.getAvatarsBatch(ids).then(res => {
                if (res.success && res.data?.avatars) {
                    setReviewerInfoMap(res.data.avatars);
                }
            }).catch(() => {});
        }
    }, [userReviews]);

    const [reviewFilter, setReviewFilter] = useState<'high' | 'low' | 'from_client' | 'from_creator' | null>(null);

    const filteredReviews = userReviews.filter(r => {
        if (!reviewFilter) return true;
        if (reviewFilter === 'high') return r.rating >= 4;
        if (reviewFilter === 'low') return r.rating < 3;
        if (reviewFilter === 'from_client') return r.role === 'client_to_creator';
        if (reviewFilter === 'from_creator') return r.role === 'creator_to_client';
        return true;
    });

    const handleTabClick = (tab: TabType) => {
        setActiveTab(tab);
        window.dispatchEvent(new CustomEvent('change_profile_tab', { detail: { tab } }));
    };

    return (
        <div>
            {/* Tab Navigation */}
            <div className="flex flex-col md:flex-row md:items-center justify-between border-b border-gray-100 mb-8 gap-4">
                <div className="flex items-center gap-8 overflow-x-auto no-scrollbar">
                    <button
                        onClick={() => handleTabClick('works')}
                        className={`pb-4 px-2 text-sm font-medium transition-all relative whitespace-nowrap ${activeTab === 'works' ? 'text-black' : 'text-gray-500 hover:text-gray-800'
                            }`}
                    >
                        <div className="flex items-center gap-2">
                            <Grid className="w-4 h-4" />
                            作品
                            <span className="bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded-full text-xs">{mergedWorksCount}</span>
                        </div>
                        {activeTab === 'works' && <span className="absolute bottom-0 left-0 w-full h-0.5 bg-black rounded-full"></span>}
                    </button>

                    <button
                        onClick={() => handleTabClick('services')}
                        className={`pb-4 px-2 text-sm font-medium transition-all relative whitespace-nowrap ${activeTab === 'services' ? 'text-black' : 'text-gray-500 hover:text-gray-800'
                            }`}
                    >
                        <div className="flex items-center gap-2">
                            <Sparkles className="w-4 h-4" />
                            服务
                            <span className="bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded-full text-xs">{servicesCount}</span>
                        </div>
                        {activeTab === 'services' && <span className="absolute bottom-0 left-0 w-full h-0.5 bg-black rounded-full"></span>}
                    </button>

                    <button
                        onClick={() => handleTabClick('briefs')}
                        className={`pb-4 px-2 text-sm font-medium transition-all relative whitespace-nowrap ${activeTab === 'briefs' ? 'text-black' : 'text-gray-500 hover:text-gray-800'
                            }`}
                    >
                        <div className="flex items-center gap-2">
                            <LayoutTemplate className="w-4 h-4" />
                            需求
                            <span className="bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded-full text-xs">{briefsCount}</span>
                        </div>
                        {activeTab === 'briefs' && <span className="absolute bottom-0 left-0 w-full h-0.5 bg-black rounded-full"></span>}
                    </button>

                    <button
                        onClick={() => handleTabClick('reviews')}
                        className={`pb-4 px-2 text-sm font-medium transition-all relative whitespace-nowrap ${activeTab === 'reviews' ? 'text-black' : 'text-gray-500 hover:text-gray-800'
                            }`}
                    >
                        <div className="flex items-center gap-2">
                            <Star className="w-4 h-4" />
                            评价
                            {reviewCount > 0 && <span className="bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded-full text-xs">{reviewCount}</span>}
                        </div>
                        {activeTab === 'reviews' && <span className="absolute bottom-0 left-0 w-full h-0.5 bg-black rounded-full"></span>}
                    </button>
                </div>

                <div className="flex items-center gap-8 pb-0 md:pb-0 justify-start md:justify-end overflow-x-auto no-scrollbar">
                    <button
                        onClick={() => handleTabClick('likes')}
                        className={`pb-4 px-2 text-sm font-medium transition-all relative whitespace-nowrap ${activeTab === 'likes' ? 'text-black' : 'text-gray-500 hover:text-gray-800'}`}
                    >
                        <div className="flex items-center gap-2">
                            <Heart className="w-4 h-4" />
                            点赞
                            <span className="bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded-full text-xs">{likes.length}</span>
                        </div>
                        {activeTab === 'likes' && <span className="absolute bottom-0 left-0 w-full h-0.5 bg-black rounded-full"></span>}
                    </button>
                    <button
                        onClick={() => handleTabClick('favorites')}
                        className={`pb-4 px-2 text-sm font-medium transition-all relative whitespace-nowrap ${activeTab === 'favorites' ? 'text-black' : 'text-gray-500 hover:text-gray-800'}`}
                    >
                        <div className="flex items-center gap-2">
                            <Bookmark className="w-4 h-4" />
                            收藏
                            <span className="bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded-full text-xs">{favorites.length}</span>
                        </div>
                        {activeTab === 'favorites' && <span className="absolute bottom-0 left-0 w-full h-0.5 bg-black rounded-full"></span>}
                    </button>
                </div>
            </div>

            {/* Content Area */}
            <div className="min-h-[400px]">
                {activeTab === 'works' && (
                    <WorkGrids
                        works={creatorWorks}
                        isLoading={creatorWorksLoading}
                        userId={userInfo?.id}
                        isOwnProfile={isOwnProfile}
                        fallbackAuthor={userInfo}
                        includePendingUploads={isOwnProfile}
                        initialOpenWorkId={shareState.postId}
                    />
                )}
                {activeTab === 'likes' && (
                    <WorkGrids
                        works={likes}
                        isLoading={interactionsLoading}
                        userId={userInfo?.id}
                    />
                )}
                {activeTab === 'favorites' && (
                    <WorkGrids
                        works={favorites}
                        isLoading={interactionsLoading}
                        userId={userInfo?.id}
                    />
                )}
                {activeTab === 'services' && (
                    <ServiceGrids
                        isOwnProfile={isOwnProfile}
                        userInfo={userInfo}
                        initialOpenServiceId={shareState.serviceId}
                    />
                )}
                {activeTab === 'briefs' && (
                    <BriefGrids
                        isOwnProfile={isOwnProfile}
                        userInfo={userInfo}
                        initialOpenBriefId={shareState.briefId}
                    />
                )}
                {activeTab === 'reviews' && (
                    <div className="space-y-4">
                        {averageRating > 0 && (
                            <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
                                <span className="text-3xl font-bold text-yellow-500">{averageRating}</span>
                                <div>
                                    <div className="flex gap-0.5">
                                        {[1, 2, 3, 4, 5].map(s => (
                                            <Star key={s} className={`w-4 h-4 ${s <= Math.round(averageRating) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-200'}`} />
                                        ))}
                                    </div>
                                    <p className="text-xs text-gray-500 mt-0.5">{reviewCount} 条评价</p>
                                </div>
                            </div>
                        )}
                        {/* Filters */}
                        <div className="flex gap-2">
                            {([
                                { key: null, label: '全部' },
                                { key: 'high', label: '好评 ≥4星' },
                                { key: 'low', label: '差评 <3星' },
                                { key: 'from_client', label: '来自客户' },
                                { key: 'from_creator', label: '来自创作者' },
                            ] as const).map(f => (
                                <button
                                    key={f.key || 'all'}
                                    type="button"
                                    onClick={() => setReviewFilter(f.key)}
                                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${reviewFilter === f.key ? 'bg-black text-white border-black' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`}
                                >
                                    {f.label}
                                </button>
                            ))}
                        </div>
                        {filteredReviews.length === 0 ? (
                            <div className="py-10 text-center text-gray-400 text-sm">暂无评价</div>
                        ) : (
                            filteredReviews.map(r => (
                                <ReviewCard
                                    key={r.contractId + r.reviewerUserId}
                                    review={r}
                                    reviewerName={reviewerInfoMap[r.reviewerUserId]?.nickname}
                                    reviewerAvatar={reviewerInfoMap[r.reviewerUserId]?.avatarUrl}
                                />
                            ))
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ProfileTabs;
